import { sorted } from "helpers";
import { MaterialLimit } from "src/store/api/generatedApi";

export const coefficientSignature = (materialLimit: MaterialLimit): string =>
  sorted(
    materialLimit.coefficients,
    (coefficient) => coefficient.material_constraint_class_id
  )
    .map(
      (coefficient) =>
        `${coefficient.material_constraint_class_id}:${coefficient.coefficient.toFixed(3)}`
    )
    .join(",");

export const materialLimitKey = (materialLimit: MaterialLimit): string =>
  `${materialLimit.chef_group_id}::${coefficientSignature(materialLimit)}`;

export const mergeMaterialLimits = (
  base: MaterialLimit[],
  overrides: MaterialLimit[]
): MaterialLimit[] =>
  Array.from(
    new Map(
      [...base, ...overrides].map((materialLimit) => [
        materialLimitKey(materialLimit),
        materialLimit,
      ])
    ).values()
  );

export const isMaterialLimitNullified = (
  materialLimit: MaterialLimit
): boolean =>
  materialLimit.bounds.every(
    (bound) =>
      bound.min_mass == null &&
      bound.max_mass == null &&
      bound.soft_min_mass == null &&
      bound.soft_max_mass == null
  );

export const nullifiedMaterialLimit = (
  materialLimit: MaterialLimit
): MaterialLimit => ({
  chef_group_id: materialLimit.chef_group_id,
  name: materialLimit.name,
  hardness: materialLimit.hardness,
  coefficients: materialLimit.coefficients,
  bounds: [],
});

export const applyMaterialLimitEdit = (
  overrides: MaterialLimit[],
  {
    original,
    updated,
    baseKeys,
  }: { original: MaterialLimit; updated: MaterialLimit; baseKeys: Set<string> }
): MaterialLimit[] => {
  const originalKey = materialLimitKey(original);
  const updatedKey = materialLimitKey(updated);
  const withoutEdited = overrides.filter((materialLimit) => {
    const key = materialLimitKey(materialLimit);
    return key !== originalKey && key !== updatedKey;
  });
  const nullifiers =
    originalKey !== updatedKey && baseKeys.has(originalKey)
      ? [nullifiedMaterialLimit(original)]
      : [];
  return [...withoutEdited, ...nullifiers, updated];
};

export const applyMaterialLimitDelete = (
  overrides: MaterialLimit[],
  { target, baseKeys }: { target: MaterialLimit; baseKeys: Set<string> }
): MaterialLimit[] => {
  const targetKey = materialLimitKey(target);
  const withoutTarget = overrides.filter(
    (materialLimit) => materialLimitKey(materialLimit) !== targetKey
  );
  const nullifiers = baseKeys.has(targetKey)
    ? [nullifiedMaterialLimit(target)]
    : [];
  return [...withoutTarget, ...nullifiers];
};
